<template>
  <b-modal size="xl" id="actual-route-modal" @show="getRoute">
    <base-spinner v-if="loading" />
    <div v-else>
      <b-alert :show="error && error.length ? true : false" variant="danger">
        <div v-if="Array.isArray(error)">
          <div class="d-flex flex-column my-1" v-for="(error_text, index) in error" :key="index">
            {{ error_text }}
          </div>
        </div>
        <div v-else>
          <div v-html="error || '&nbsp;'"></div>
        </div>
      </b-alert>
      <div id="map" style="height: 500px"></div>
      <div
        class="d-flex align-items-center justify-content-center flex-wrap p-3"
        v-if="type === 'third-party' || type === 'mobile'"
      >
        <div class="d-flex align-items-center justify-content-center flex-wrap p-3 gap-4">
          <p class="route-name">
            {{ type === 'third-party' ? $t('bus-route') : $t('mobile-route') }} :
          </p>
          <div
            v-for="(route, index) in route_labels"
            :key="index"
            class="me-5 d-flex align-items-center"
          >
            <div class="box mx-2" :style="{ 'background-color': route?.color }"></div>
            <div>
              {{ route?.label }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="d-flex flex-column align-items-center justify-content-center flex-wrap p-3"
        v-else
      >
        <div class="d-flex align-items-center justify-content-center flex-wrap p-3 gap-4">
          <p class="route-name">{{ $t('bus-route') }} :</p>
          <div
            v-for="(route, index) in route_labels"
            :key="index"
            class="me-5 d-flex align-items-center"
          >
            <div class="box mx-2" :style="{ 'background-color': route?.color }"></div>
            <div>
              {{ route?.label }}
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-center gap-4 flex-wrap p-3">
          <p class="route-name">{{ $t('mobile-route') }} :</p>
          <div
            v-for="(route, index) in route_labels_second"
            :key="index"
            class="me-5 d-flex align-items-center"
          >
            <div class="box mx-2" :style="{ 'background-color': route?.color }"></div>
            <div>
              {{ route?.label }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import TripRouteService from '@/services/trip-route.service'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

let map, start_marker, end_marker, yard_marker

export default {
  props: ['data', 'type'],
  data() {
    return {
      loading: false,
      error: null,
      routes: [],
      route_labels: [
        {
          label: this.$t('navigating-to-pickup'),
          value: 'NavigatingToPickup',
          color: 'red',
        },
        {
          label: this.$t('navigating-to-dropoff'),
          value: 'NavigatingToDropOff',
          color: 'green',
        },
        {
          label: this.$t('navigating-to-yard'),
          value: 'NavigatingToYard',
          color: 'orange',
        },
      ],
      route_labels_second: [
        {
          label: this.$t('navigating-to-pickup'),
          value: 'NavigatingToPickup',
          color: 'grey',
        },
        {
          label: this.$t('navigating-to-dropoff'),
          value: 'NavigatingToDropOff',
          color: 'blue',
        },
        {
          label: this.$t('navigating-to-yard'),
          value: 'NavigatingToYard',
          color: 'cyan',
        },
      ],
    }
  },
  methods: {
    loadMap() {
      setTimeout(() => {
        if (document.getElementById('map')) {
          mapboxgl.accessToken = this.$config.mapBoxPK

          // Check if the RTL plugin has already been loaded
          if (mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
            mapboxgl.setRTLTextPlugin(
              'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
              null,
              true // Lazy load the plugin
            )
          }

          map = new mapboxgl.Map({
            container: 'map',
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [46.715022884817245, 24.642516582053403],
            zoom: 8,
            language: this.isLangRtl ? 'ar' : 'en',
          })

          map.on('style.load', async () => {
            if (this.data?.startLocation?.location) {
              const start_marker_el = document.createElement('div')
              start_marker_el.className = 'start-marker'
              if (start_marker) start_marker.remove()
              start_marker = new mapboxgl.Marker(start_marker_el)
                .setLngLat(this.data.startLocation.location)
                .setPopup(
                  new mapboxgl.Popup({ offset: 25 }).setHTML(
                    `<div class='p-1'>
                      <h6 class='text-center'>${this.$t('pick-up-location')}</h6>
                      <div>${this.data.startLocation.name}</div>
                    </div>`
                  )
                )
                .addTo(map)
            }

            if (this.data?.endLocation?.location) {
              const end_marker_el = document.createElement('div')
              end_marker_el.className = 'start-marker'
              if (end_marker) end_marker.remove()
              end_marker = new mapboxgl.Marker(end_marker_el)
                .setLngLat(this.data?.endLocation?.location)
                .setPopup(
                  new mapboxgl.Popup({ offset: 25 }).setHTML(
                    `<div class='p-1'>
                      <h6 class='text-center'>${this.$t('drop-off-location')}</h6>
                      <div>${this.data?.endLocation?.name}</div>
                    </div>`
                  )
                )
                .addTo(map)
            }

            if (this.data?.driver?.garage?.garageLocation?.location) {
              const yard_marker_el = document.createElement('div')
              yard_marker_el.className = 'start-marker'
              if (yard_marker) yard_marker.remove()
              yard_marker = new mapboxgl.Marker(yard_marker_el)
                .setLngLat(this.data.driver.garage.garageLocation.location)
                .setPopup(
                  new mapboxgl.Popup({ offset: 25 }).setHTML(
                    `<div class='p-1'>
                      <h6 class='text-center'>${this.$t('garage')}</h6>
                      <div>${this.data.driver.garage.garageLocation.name}</div>
                    </div>`
                  )
                )
                .addTo(map)
            }

            map.fitBounds([this.data?.startLocation?.location, this.data?.endLocation?.location])

            if (this.routes.length && this.type !== 'both') {
              for (let i = 0; i < this.routes.length; i++) {
                const element = this.routes[i]
                const geojson = {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: element.route,
                  },
                }

                if (map.getSource(element.currentNavigation)) {
                  map.getSource(element.currentNavigation).setData(geojson)
                } else {
                  map?.addLayer({
                    id: element.currentNavigation,
                    type: 'line',
                    source: {
                      type: 'geojson',
                      data: geojson,
                    },
                    layout: {
                      'line-join': 'round',
                      'line-cap': 'round',
                    },
                    paint: {
                      'line-color': this.route_labels?.find(
                        (each) => each.value === element.currentNavigation
                      )?.color,
                      'line-width': 3,
                      'line-opacity': 1,
                    },
                  })
                }
              }
            } else if (this.routes.length && this.type === 'both') {
              for (let i = 0; i < this.routes[0].length; i++) {
                const element = this.routes[0][i]
                const geojson = {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: element.route,
                  },
                }

                if (map.getSource(element.currentNavigation)) {
                  map.getSource(element.currentNavigation).setData(geojson)
                } else {
                  map?.addLayer({
                    id: element.currentNavigation,
                    type: 'line',
                    source: {
                      type: 'geojson',
                      data: geojson,
                    },
                    layout: {
                      'line-join': 'round',
                      'line-cap': 'round',
                    },
                    paint: {
                      'line-color': this.route_labels?.find(
                        (each) => each.value === element.currentNavigation
                      )?.color,
                      'line-width': 4,
                      'line-opacity': 1,
                    },
                  })
                }
              }

              if (this.routes.length == 2) {
                for (let j = 0; j < this.routes[1].length; j++) {
                  const element = this.routes[1][j]
                  const geojson = {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                      type: 'LineString',
                      coordinates: element.route,
                    },
                  }

                  map?.addLayer({
                    id: element.currentNavigation + j,
                    type: 'line',
                    source: {
                      type: 'geojson',
                      data: geojson,
                    },
                    layout: {
                      'line-join': 'round',
                      'line-cap': 'round',
                    },
                    paint: {
                      'line-color': this.route_labels_second?.find(
                        (each) => each.value === element.currentNavigation
                      )?.color,
                      'line-width': 4,
                      'line-opacity': 1,
                    },
                  })
                }
              }
            }
          })
        }
      })
    },
    getRoute() {
      this.loading = true
      this.error = null
      this.routes = []

      if (this.type !== 'both') {
        let params = new URLSearchParams()
        params.append('type', this.type)

        TripRouteService.get(this.data._id, params)
          .then((res) => {
            this.routes = res
          })
          .catch(({ message }) => {
            this.error = message
          })
          .finally(() => {
            this.loading = false
            this.loadMap()
          })
      } else {
        let params = new URLSearchParams()
        params.append('type', 'third-party')

        TripRouteService.get(this.data._id, params)
          .then((res) => {
            this.routes = res
            params.delete('type')
            params.append('type', 'mobile')

            TripRouteService.get(this.data._id, params)
              .then((res) => {
                const temp = res

                // Add offsets in case of more than one route
                const updatedTrips = temp.map((trip) => {
                  trip.route = trip.route.map((coord) => {
                    if (Array.isArray(coord) && coord.length === 2) {
                      return [coord[0] + 0.000045, coord[1] + 0.000045] // Add 0.0002 to lat and lon
                    }
                    return coord
                  })
                  return trip
                })

                this.routes = temp.length ? [this.routes, updatedTrips] : [this.routes]
              })
              .catch(({ message }) => {
                this.error = message
              })
              .finally(() => {
                this.loading = false
                this.loadMap()
              })
          })
          .catch(({ message }) => {
            this.error = message
            this.loading = false
          })
      }
    },
  },
  i18n: {
    messages: {
      en: require('@/services/i18n/locales/en/manageTrips.json'),
      ar: require('@/services/i18n/locales/ar/manageTrips.json'),
    },
  },
}
</script>

<style lang="scss" scoped>
.box {
  height: 10px;
  width: 20px;
}
</style>

<style lang="scss">
.start-marker {
  background-image: url('@/assets/images/icons/start-marker.svg');
  background-size: cover;
  width: 30px;
  height: 30px;
  cursor: pointer;
}

.modal-body {
  padding: 0px !important;
}

.route-name {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}
</style>
